// Card #1652 (design §6.1) — Ranked list family, mode types 6 (Mid-Class
// Result) and 7 (End of Class Result): "class header + ranked rows +
// banner." `rankMode` is the wording gate (design §4.5): type 6 is NOT
// end-of-class yet, so its rows say "Rank"; type 7 is the one case §4.5
// permits "place"/"placing" wording, so its rows say "Place". (Card #1794's
// pare-down removed type 6 entirely — End of Class Result, type 7, is now
// this family's only surviving caller — but the component stays mode-
// agnostic rather than hardcoding a wording it no longer varies on.)
//
// Card #1791 — a class can run past what one column holds at projector
// legibility (WEC 341848 class 215: 19 rides, a NORMAL size, not an
// outlier). Rows split into fixed groups of 10, side by side (Jaybill,
// 2026-08-20: "two columns on one screen, NOT a rotating/paginated
// carousel"), reading top-to-bottom then left-to-right (AC2): column N
// holds rows [10N, 10N+10). A class of 10 or fewer stays a single column
// (AC3) because it only ever produces one chunk, so no wrapper and no
// half-empty second column exist for it to render.
//
// Past 20 rows is the card's own "open, needs a decision during the work."
// Decision made here: keep chunking in groups of 10 rather than capping at
// two columns or introducing scrolling — a 21st+ rider gets a third column
// instead of being silently dropped (AC4). The board is an unattended,
// public display with no error affordance, so a wider-than-usual layout is
// the safer failure mode over a truncated ranked list.
import ClassHeader from "./ClassHeader";
import RankedList, { computeIdentityColumns, computeShowScoreColumn } from "./RankedList";
import Banner from "./Banner";
import "./RankedListLayout.css";

const GROUP_SIZE = 10;

function chunkRankedList(rankedList) {
  if (!Array.isArray(rankedList) || !rankedList.length) return [];
  const chunks = [];
  for (let start = 0; start < rankedList.length; start += GROUP_SIZE) {
    chunks.push(rankedList.slice(start, start + GROUP_SIZE));
  }
  return chunks;
}

// Card #1945 — FVD has no literal "division" field; docs/score/fvd-data-
// model.md §1 (#756, RESOLVED) is "division" == rider status: class-level
// Open/AA/JR-YR/Other eligibility bit-flags, foxbox-derived onto each
// entry as `entryIdentity.riderStatus`. That doc entry describes the
// CLASS-level `tblClass` derivation; the value actually grouped on here is
// per-RIDER (`tblRider.chrStatus`), whose authority is
// apps/score-api/src/handlers/ride.js:447-452 (#821/#784, which is
// explicit that "Class.division... is a per-CLASS eligibility derivation,
// not per-rider" — the two are synonymous in practice but this grouping
// reads the per-rider field, not the class-level one). score-api's
// composeRankedList already carries that per row (composeEntryIdentity) —
// grouping it here is a pure FE presentation concern, no new payload key,
// no score-api change.
//
// Case-insensitive (trim + lowercase) because the SAME FVD class spells one
// division two ways in real data (`Jr/YR` vs `JR/YR`, WEC class 119) and
// that must collapse to ONE table (AC3), never split on casing alone. The
// FIRST-seen original casing is kept as the display name — a WEC director
// reads whichever spelling foxbox happened to write first, not a re-cased
// label manufactured here.
function divisionKeyOf(row) {
  const status = row?.entryIdentity?.riderStatus;
  return typeof status === "string" && status.trim() ? status.trim().toLowerCase() : null;
}

// A row with no riderStatus at all groups under `key: null` — its own
// headingless bucket, in original stable order, rather than being merged
// into a named division it doesn't belong to or silently dropped. The docs
// say every entry in a division-flagged class gets a status, so this branch
// is defensive, not an expected shape.
function groupRankedListByDivision(rankedList) {
  if (!Array.isArray(rankedList)) return [];
  const groups = [];
  const indexByKey = new Map();
  for (const row of rankedList) {
    const key = divisionKeyOf(row);
    if (!indexByKey.has(key)) {
      indexByKey.set(key, groups.length);
      groups.push({ key, name: key == null ? null : row.entryIdentity.riderStatus.trim(), rows: [] });
    }
    groups[indexByKey.get(key)].rows.push(row);
  }
  return groups;
}

export default function RankedListLayout({ fields, banner, rankMode }) {
  if (!fields) return null;

  const rankedList = fields.rankedList;
  // Column headings (#1780, AC5) and the Score column's own #1780 AC5
  // presence rule (#1944) must agree across every sibling table — computed
  // ONCE against the FULL class list and passed into every table, exactly
  // #1791's "compute once, apply to every chunk" rule for the groups-of-10
  // split. Design decision (card #1945): this now also spans every
  // DIVISION's table, not just chunk-columns within one division. Divisions
  // rendered side by side are still siblings of ONE class result — letting
  // one division show a Horse column the other doesn't would read as a data
  // inconsistency rather than the coincidence it actually is.
  const columns = computeIdentityColumns(rankedList);
  const showScore = computeShowScoreColumn(rankedList);

  function renderScope(rows) {
    const chunks = chunkRankedList(rows);
    if (chunks.length > 1) {
      return (
        <div className="scoreboard-ranked-list__columns" data-testid="scoreboard-ranked-list-columns">
          {chunks.map((chunk, index) => (
            <RankedList key={index} rankedList={chunk} rankMode={rankMode} columns={columns} showScore={showScore} />
          ))}
        </div>
      );
    }
    return <RankedList rankedList={rows} rankMode={rankMode} columns={columns} showScore={showScore} />;
  }

  // AC2 — a class where NO row carries a division value renders exactly as
  // before: one scope, no `__divisions` wrapper, no DIVISION heading at all.
  // Design decision (card #1945, the #1780 "decide scope deliberately"
  // note): the gate is "does this class carry ANY division data", not "are
  // there 2+ distinct divisions" — a class with exactly one division value
  // on every row (AC6's "1-division class") still HAS division data, so it
  // still renders under a DIVISION heading, just as a single table with
  // nothing beside it to disambiguate from.
  const groups = groupRankedListByDivision(rankedList);
  const hasDivisions = groups.some((group) => group.key != null);

  const rankedListBody = hasDivisions ? (
    <div className="scoreboard-ranked-list__divisions" data-testid="scoreboard-ranked-list-divisions">
      {groups.map((group, index) => (
        <div
          key={group.key ?? `__none-${index}`}
          className="scoreboard-ranked-list__division"
          data-testid="scoreboard-ranked-list-division"
        >
          {group.name && (
            <div
              className="scoreboard-ranked-list__division-heading"
              data-testid="scoreboard-ranked-list-division-heading"
            >
              <span className="scoreboard-ranked-list__division-label">DIVISION</span>
              <span
                className="scoreboard-ranked-list__division-name"
                data-testid="scoreboard-ranked-list-division-name"
              >
                {group.name}
              </span>
            </div>
          )}
          {renderScope(group.rows)}
        </div>
      ))}
    </div>
  ) : (
    renderScope(rankedList)
  );

  // Card #1949 (parity with the approved demo pages, card #1928): the
  // banner leads the layout instead of closing it. FINAL CLASS RESULTS is
  // the first thing read at distance, above the class number/name, not a
  // trailing footnote under the table.
  return (
    <div className="scoreboard-ranked-list" data-testid="scoreboard-ranked-list">
      <Banner banner={banner} />
      <ClassHeader classNumberName={fields.classNumberName} judgesPositions={fields.judgesPositions} />
      {rankedListBody}
    </div>
  );
}
